import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useLoading } from '../contexts/LoadingContext';
import LoadingButton from '../components/LoadingButton';
import axios from 'axios';
import { 
  Plus, 
  Search, 
  BookOpen, 
  MapPin, 
  User, 
  Filter,
  Grid,
  List,
  Heart,
  Eye,
  Clock,
  Shield,
  ChevronDown,
  ChevronUp,
  X,
  Tag,
  Globe,
  Book,
  Calendar,
  TrendingUp,
  SortAsc,
  SortDesc,
  Check,
  Trash2,
  UserPlus,
  AlertTriangle
} from 'lucide-react';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FavoriteButton from '../components/FavoriteButton';
import FavoriteUsersModal from '../components/FavoriteUsersModal';
import UpgradeModal from '../components/UpgradeModal';
import { useAuth } from '../contexts/AuthContext';
import { isOverdue, formatOverdueInfo, formatRentalDate } from '../utils/dateUtils';

interface Book {
  id: string;
  title: string;
  author: string;
  cover_image: string;
  is_available: boolean;
  owner_id?: string;
  owner_name?: string;
  owner_city?: string;
  distance?: number;
  distance_formatted?: string;
  created_at: string;
  has_requested?: boolean;
  rental_info?: {
    start_date: string;
    end_date: string;
    duration_days: number;
    renter_name: string;
  };
  // Nouvelles propriétés pour le filtrage
  google_id?: string;
  language?: string;
  categories?: string[];
  page_count?: number;
  published_date?: string;
  rental_count?: number; // Nombre de fois que le livre a été loué
  isbn?: string;
}

const Library: React.FC = () => {
  const { user } = useAuth();
  const { showSuccess, showError, showDelete, showBook } = useToast();
  const { isLoading, setLoading: setLoadingState, withLoading } = useLoading();
  const [availableBooks, setAvailableBooks] = useState<Book[]>([]);
  const [myBooks, setMyBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'available' | 'my-library'>('available');
  const [radiusFilter, setRadiusFilter] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Nouveaux états pour les filtres avancés
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [languageFilter, setLanguageFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'recent' | 'popular' | 'distance' | 'title' | 'author'>('recent');
  const [pageCountFilter, setPageCountFilter] = useState<{min: number | null, max: number | null}>({min: null, max: null});
  const [yearFilter, setYearFilter] = useState<{min: number | null, max: number | null}>({min: null, max: null});
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    bookId: string;
    bookTitle: string;
  }>({
    isOpen: false,
    bookId: '',
    bookTitle: ''
  });
  const [favoriteUsersModal, setFavoriteUsersModal] = useState<{
    isOpen: boolean;
    bookId: string;
    bookTitle: string;
  }>({
    isOpen: false,
    bookId: '',
    bookTitle: ''
  });
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    fetchBooks();
  }, [radiusFilter]);

  const fetchBooks = async () => {
    setLoading(true);
    
    const normalize = (book: any) => ({
      ...book,
      is_available: Boolean(book.is_available),
    });

    try {
      const [allResult, mineResult] = await Promise.allSettled([
        axios.get(`/api/books/all${radiusFilter ? `?radius=${radiusFilter}` : ''}`),
        axios.get('/api/books/my-library'),
      ]);

      if (allResult.status === 'fulfilled') {
        setAvailableBooks(allResult.value.data.map(normalize));
      } else {
        console.error('Fetch catalogue error:', allResult.reason);
        showError('Catalogue', 'Impossible de charger le catalogue pour le moment.');
      }

      if (mineResult.status === 'fulfilled') {
        setMyBooks(mineResult.value.data.map(normalize));
      } else {
        console.error('Fetch my library error:', mineResult.reason);
        setMyBooks([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleBookAvailability = withLoading('toggle-availability', async (bookId: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/api/books/${bookId}/availability`, {
        is_available: !currentStatus
      });
      
      // Update the book status in myBooks
      setMyBooks(prev => prev.map(book => 
        book.id === bookId 
          ? { ...book, is_available: !currentStatus }
          : book
      ));
      
      // Note: We don't modify availableBooks here because it should only contain
      // books from other users. The availableBooks list is fetched separately
      // and will be updated when the user switches tabs or refreshes.
      
    } catch (error: any) {
      showError('Erreur', 'Erreur lors de la mise à jour de la disponibilité');
    }
  });

  const openDeleteModal = (bookId: string, bookTitle: string) => {
    setDeleteModal({
      isOpen: true,
      bookId,
      bookTitle
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      bookId: '',
      bookTitle: ''
    });
  };

  const openFavoriteUsersModal = (bookId: string, bookTitle: string) => {
    setFavoriteUsersModal({
      isOpen: true,
      bookId,
      bookTitle
    });
  };

  const closeFavoriteUsersModal = () => {
    setFavoriteUsersModal({
      isOpen: false,
      bookId: '',
      bookTitle: ''
    });
  };

  const confirmDeleteBook = withLoading('delete-book', async () => {
    try {
      await axios.delete(`/api/books/${deleteModal.bookId}`);
      setMyBooks(prev => prev.filter(book => book.id !== deleteModal.bookId));
      setAvailableBooks(prev => prev.filter(book => book.id !== deleteModal.bookId));
      closeDeleteModal();
      showDelete('Livre supprimé', 'Le livre a été retiré de votre bibliothèque');
    } catch (error: any) {
      showError('Erreur', 'Erreur lors de la suppression du livre');
      closeDeleteModal();
    }
  });

  const requestBook = async (bookId: string) => {
    const loadingKey = `request-book-${bookId}`;
    
    setLoadingState(loadingKey, true);
    try {
      await axios.post(`/api/users/request-book/${bookId}`);
      
      // Mettre à jour l'état local pour indiquer que la demande a été faite
      setAvailableBooks(prev => prev.map(book => 
        book.id === bookId 
          ? { ...book, has_requested: true }
          : book
      ));
      
      showBook('Demande envoyée', 'Votre demande d\'échange a été envoyée');
    } catch (error: any) {
      if (error.response?.status === 403 && error.response?.data?.code === 'BORROW_LIMIT') {
        setUpgradeOpen(true);
      } else if (error.message === 'Request timeout') {
        showError('Erreur', 'La demande a pris trop de temps. Vérifiez vos demandes pour voir si elle a été envoyée.');
      } else {
        showError('Erreur', error.response?.data?.error || 'Erreur lors de la demande d\'emprunt');
      }
    } finally {
      setLoadingState(loadingKey, false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatRentalDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Fonction pour formater le nom (prénom + initiale du nom)
  const formatOwnerName = (fullName: string) => {
    const nameParts = fullName.trim().split(' ');
    if (nameParts.length === 1) {
      return nameParts[0]; // Si un seul mot, on l'affiche tel quel
    }
    const firstName = nameParts[0];
    const lastNameInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return `${firstName} ${lastNameInitial}.`;
  };

  const getDaysUntilReturn = (endDate: string) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Fonction pour extraire l'année de publication
  const getPublicationYear = (publishedDate: string) => {
    if (!publishedDate) return null;
    const year = new Date(publishedDate).getFullYear();
    return isNaN(year) ? null : year;
  };

  // Fonction pour obtenir toutes les catégories uniques
  const getAllCategories = (books: Book[]) => {
    const categories = new Set<string>();
    books.forEach(book => {
      if (book.categories) {
        book.categories.forEach(category => categories.add(category));
      }
    });
    return Array.from(categories).sort();
  };

  // Fonction pour obtenir toutes les langues uniques
  const getAllLanguages = (books: Book[]) => {
    const languages = new Set<string>();
    books.forEach(book => {
      if (book.language && book.language !== 'unknown') {
        languages.add(book.language);
      }
    });
    return Array.from(languages).sort();
  };

  // Fonction de filtrage avancée
  const filterBooks = (books: Book[]) => {
    return books.filter(book => {
      // Filtre par recherche textuelle
      const matchesSearch = !searchQuery || 
        book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        book.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (book.isbn && book.isbn.includes(searchQuery));

      // Filtre par catégorie
      const matchesCategory = !categoryFilter || 
        (book.categories && book.categories.includes(categoryFilter));

      // Filtre par langue
      const matchesLanguage = !languageFilter || 
        book.language === languageFilter;

      // Filtre par nombre de pages
      const matchesPageCount = !book.page_count || 
        ((!pageCountFilter.min || book.page_count >= pageCountFilter.min) &&
        (!pageCountFilter.max || book.page_count <= pageCountFilter.max));

      // Filtre par année de publication
      const bookYear = getPublicationYear(book.published_date || '');
      const matchesYear = !bookYear ||
        ((!yearFilter.min || bookYear >= yearFilter.min) &&
        (!yearFilter.max || bookYear <= yearFilter.max));

      return matchesSearch && matchesCategory && matchesLanguage && matchesPageCount && matchesYear;
    });
  };

  // Fonction de tri
  const sortBooks = (books: Book[]) => {
    return [...books].sort((a, b) => {
      switch (sortBy) {
        case 'popular':
          return (b.rental_count || 0) - (a.rental_count || 0);
        case 'distance':
          if ((a.distance === null || a.distance === undefined) && (b.distance === null || b.distance === undefined)) return 0;
          if (a.distance === null || a.distance === undefined) return 1;
          if (b.distance === null || b.distance === undefined) return -1;
          return a.distance - b.distance;
        case 'title':
          return a.title.localeCompare(b.title);
        case 'author':
          return a.author.localeCompare(b.author);
        case 'recent':
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });
  };

  // Appliquer les filtres et le tri
  const filteredAvailableBooks = sortBooks(filterBooks(availableBooks));
  const filteredMyBooks = sortBooks(filterBooks(myBooks));

  // Fonction pour réinitialiser tous les filtres
  const resetAllFilters = () => {
    setSearchQuery('');
    setCategoryFilter(null);
    setLanguageFilter(null);
    setPageCountFilter({min: null, max: null});
    setYearFilter({min: null, max: null});
    setSortBy('recent');
    setRadiusFilter(null);
  };

  // Fonction pour compter les filtres actifs
  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchQuery) count++;
    if (categoryFilter) count++;
    if (languageFilter) count++;
    if (pageCountFilter.min || pageCountFilter.max) count++;
    if (yearFilter.min || yearFilter.max) count++;
    if (radiusFilter) count++;
    return count;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-stone-200 border-t-forest-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de votre bibliothèque...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-6 lg:mb-0">
              <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-ink mb-2">
                Bibliothèque communautaire
              </h1>
              <p className="text-base sm:text-lg text-gray-600">
                Découvrez et partagez des livres avec des passionnés près de chez vous
              </p>
            </div>
            <Link
              to="/add-book"
              className="btn btn-primary text-sm sm:text-base"
            >
              <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
              Ajouter un livre
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rechercher un livre
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-forest-600 transition-colors text-sm sm:text-base"
                  placeholder="Titre, auteur ou ISBN..."
                />
              </div>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trier par
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-forest-600 transition-colors text-sm sm:text-base"
              >
                <option value="recent">Plus récents</option>
                <option value="popular">Plus populaires</option>
                <option value="distance">Plus proches</option>
                <option value="title">Titre (A-Z)</option>
                <option value="author">Auteur (A-Z)</option>
              </select>
            </div>

            {/* Advanced Filters Toggle */}
            <div className="flex items-end">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="w-full inline-flex items-center justify-center px-4 py-2 sm:py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm sm:text-base"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filtres avancés
                {getActiveFiltersCount() > 0 && (
                  <span className="ml-2 bg-forest-600 text-white text-xs rounded-full px-2 py-0.5">
                    {getActiveFiltersCount()}
                  </span>
                )}
                {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
              </button>
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Tag className="h-4 w-4 inline mr-1" />
                    Catégorie
                  </label>
                  <select
                    value={categoryFilter || ''}
                    onChange={(e) => setCategoryFilter(e.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-forest-600 transition-colors text-sm"
                  >
                    <option value="">Toutes les catégories</option>
                    {getAllCategories(availableBooks).map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                {/* Language Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Globe className="h-4 w-4 inline mr-1" />
                    Langue
                  </label>
                  <select
                    value={languageFilter || ''}
                    onChange={(e) => setLanguageFilter(e.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-forest-600 transition-colors text-sm"
                  >
                    <option value="">Toutes les langues</option>
                    {getAllLanguages(availableBooks).map(language => (
                      <option key={language} value={language}>{language}</option>
                    ))}
                  </select>
                </div>

                {/* Page Count Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Book className="h-4 w-4 inline mr-1" />
                    Nombre de pages
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={pageCountFilter.min || ''}
                      onChange={(e) => setPageCountFilter(prev => ({...prev, min: e.target.value ? parseInt(e.target.value) : null}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-forest-600 transition-colors text-sm"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={pageCountFilter.max || ''}
                      onChange={(e) => setPageCountFilter(prev => ({...prev, max: e.target.value ? parseInt(e.target.value) : null}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-forest-600 transition-colors text-sm"
                    />
                  </div>
                </div>

                {/* Year Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="h-4 w-4 inline mr-1" />
                    Année de publication
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={yearFilter.min || ''}
                      onChange={(e) => setYearFilter(prev => ({...prev, min: e.target.value ? parseInt(e.target.value) : null}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-forest-600 transition-colors text-sm"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={yearFilter.max || ''}
                      onChange={(e) => setYearFilter(prev => ({...prev, max: e.target.value ? parseInt(e.target.value) : null}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-forest-600 transition-colors text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Radius Filter */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="h-4 w-4 inline mr-1" />
                  Rayon de recherche
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setRadiusFilter(5)}
                    className={`px-3 py-1.5 text-xs rounded-lg transition-all duration-200 ${
                      radiusFilter === 5 
                        ? 'bg-forest-600 text-white shadow-md' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    5 km
                  </button>
                  <button
                    onClick={() => setRadiusFilter(10)}
                    className={`px-3 py-1.5 text-xs rounded-lg transition-all duration-200 ${
                      radiusFilter === 10 
                        ? 'bg-forest-600 text-white shadow-md' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    10 km
                  </button>
                  <button
                    onClick={() => setRadiusFilter(20)}
                    className={`px-3 py-1.5 text-xs rounded-lg transition-all duration-200 ${
                      radiusFilter === 20 
                        ? 'bg-forest-600 text-white shadow-md' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    20 km
                  </button>
                  <button
                    onClick={() => setRadiusFilter(50)}
                    className={`px-3 py-1.5 text-xs rounded-lg transition-all duration-200 ${
                      radiusFilter === 50 
                        ? 'bg-forest-600 text-white shadow-md' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    50 km
                  </button>
                  {radiusFilter && (
                    <button
                      onClick={() => setRadiusFilter(null)}
                      className="px-3 py-1.5 text-xs rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-all duration-200"
                    >
                      Effacer
                    </button>
                  )}
                </div>
              </div>

              {/* Filter Actions */}
              <div className="mt-4 flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  {getActiveFiltersCount() > 0 && (
                    <span>{getActiveFiltersCount()} filtre(s) actif(s)</span>
                  )}
                </div>
                <button
                  onClick={resetAllFilters}
                  className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <X className="h-4 w-4 mr-1" />
                  Réinitialiser tous les filtres
                </button>
              </div>
            </div>
          )}

          {/* Filter Results Summary */}
          {(getActiveFiltersCount() > 0 || radiusFilter) && (
            <div className="mt-4 p-3 bg-forest-50 rounded-lg">
              <div className="flex items-center text-sm text-forest-700">
                <Filter className="h-4 w-4 mr-2" />
                {filteredAvailableBooks.length} livre{filteredAvailableBooks.length > 1 ? 's' : ''} trouvé{filteredAvailableBooks.length > 1 ? 's' : ''}
                {radiusFilter && ` dans un rayon de ${radiusFilter} km`}
                {getActiveFiltersCount() > 0 && ` avec ${getActiveFiltersCount()} filtre(s) actif(s)`}
              </div>
            </div>
          )}
        </div>

        {/* Tabs and View Mode */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          {/* Tabs */}
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-4 sm:mb-0">
            <button
              onClick={() => setActiveTab('available')}
              className={`px-3 sm:px-6 py-2 sm:py-3 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 ${
                activeTab === 'available'
                  ? 'bg-white text-forest-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="hidden sm:inline">Livres disponibles</span>
              <span className="sm:hidden">Disponibles</span> ({filteredAvailableBooks.length})
            </button>
            <button
              onClick={() => setActiveTab('my-library')}
              className={`px-3 sm:px-6 py-2 sm:py-3 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 ${
                activeTab === 'my-library'
                  ? 'bg-white text-forest-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="hidden sm:inline">Ma bibliothèque</span>
              <span className="sm:hidden">Mes livres</span> ({filteredMyBooks.length})
            </button>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-forest-100 text-forest-700' 
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <Grid className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'list' 
                  ? 'bg-forest-100 text-forest-700' 
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <List className="h-5 w-5" />
            </button>
          </div>
        </div>

         {/* Books Grid/List */}
         {viewMode === 'grid' ? (
           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
             {(activeTab === 'available' ? filteredAvailableBooks : filteredMyBooks).map((book) => (
               <div key={book.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 group flex flex-col h-full">
                 {/* Image du livre */}
                 <Link to={`/book/${book.id}`} className="block relative">
                   <div className="aspect-[3/4] overflow-hidden">
                     {book.cover_image ? (
                       <img
                         src={book.cover_image}
                         alt={book.title}
                         className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                       />
                     ) : (
                       <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                         <BookOpen className="h-16 w-16 text-gray-300" />
                       </div>
                     )}
                   </div>
                   
                   {/* Badges en overlay - simplifiés */}
                   <div className="absolute top-3 left-3 flex flex-col gap-2">
                     {/* Badge Mon livre */}
                     {activeTab === 'available' && book.owner_id === user?.id && (
                       <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium shadow-lg">
                         Mon livre
                       </span>
                     )}
                     
                     {/* Badge Populaire */}
                     {(book.rental_count || 0) >= 3 && (
                       <span className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium shadow-lg flex items-center">
                         <TrendingUp className="h-3 w-3 mr-1" />
                         Pop
                       </span>
                     )}
                   </div>
                   
                   <div className="absolute top-3 right-3 flex flex-col gap-2">
                     {/* Badge Statut */}
                     {book.is_available ? (
                       <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium shadow-lg">
                         Disponible
                       </span>
                     ) : (
                       <span className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium shadow-lg">
                         Loué
                       </span>
                     )}
                     
                     {/* Badge Demande envoyée */}
                     {activeTab === 'available' && book.is_available && book.has_requested && (
                       <span className="bg-forest-600 text-white px-2 py-1 rounded-full text-xs font-medium shadow-lg">
                         Demande envoyée
                       </span>
                     )}
                   </div>
                 </Link>
                 
                 {/* Bouton favori sous l'image */}
                 {activeTab === 'available' && book.owner_id !== user?.id && (
                   <div className="flex justify-center mt-3">
                     <FavoriteButton 
                       bookId={book.id} 
                       size="md" 
                       showCount={false}
                     />
                   </div>
                 )}
                 
                 {/* Contenu de la carte - avec flex pour pousser les boutons en bas */}
                 <div className="p-5 flex flex-col flex-grow">
                   {/* Titre et auteur */}
                   <Link to={`/book/${book.id}`} className="block mb-3">
                     <h3 className="font-semibold text-gray-900 group-hover:text-forest-700 transition-colors mb-1 text-sm line-clamp-2 leading-tight">
                       {book.title}
                     </h3>
                     <p className="text-xs text-gray-600 line-clamp-1">
                       {book.author}
                     </p>
                   </Link>
                   
                   {/* Informations propriétaire - simplifiées */}
                   {activeTab === 'available' && book.owner_name && (
                     <div className="mb-4 space-y-1">
                       <div className="flex items-center text-xs text-gray-600">
                         <User className="h-3 w-3 mr-1.5 flex-shrink-0" />
                         <span className="truncate">{formatOwnerName(book.owner_name)}</span>
                       </div>
                       {book.owner_city && (
                         <div className="flex items-center text-xs text-gray-500">
                           <MapPin className="h-3 w-3 mr-1.5 flex-shrink-0" />
                           <span className="truncate">{book.owner_city}</span>
                         </div>
                       )}
                       {book.distance_formatted && (
                         <div className="flex items-center text-xs text-forest-700 font-medium">
                           <MapPin className="h-3 w-3 mr-1.5 flex-shrink-0" />
                           <span>{book.distance_formatted}</span>
                         </div>
                       )}
                     </div>
                   )}
                   
                   {/* Actions - poussées en bas avec mt-auto */}
                   <div className="space-y-2 mt-auto">
                     {/* Action principale selon le contexte */}
                     {activeTab === 'available' && book.is_available && book.owner_id !== user?.id && (
                       <LoadingButton
                         onClick={() => requestBook(book.id)}
                         loading={isLoading(`request-book-${book.id}`)}
                         loadingText="Envoi en cours..."
                         disabled={book.has_requested}
                         className="w-full"
                         variant={book.has_requested ? 'secondary' : 'primary'}
                       >
                         {book.has_requested ? (
                           <>
                             <Clock className="h-4 w-4 mr-2" />
                             Demande en cours
                           </>
                         ) : (
                           <>
                             <UserPlus className="h-4 w-4 mr-2" />
                             Demander
                           </>
                         )}
                       </LoadingButton>
                     )}
                     
                     
                     {/* Bouton pour mes propres livres */}
                     {activeTab === 'available' && book.owner_id === user?.id && (
                       <button
                         onClick={() => openFavoriteUsersModal(book.id, book.title)}
                         className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-forest-600 text-white text-sm font-medium rounded-xl hover:bg-forest-700 hover:shadow-md transition-all duration-200"
                       >
                         <Heart className="h-4 w-4 mr-2" />
                         Voir les intéressés
                       </button>
                     )}
                     
                     {/* Actions pour Ma bibliothèque */}
                     {activeTab === 'my-library' && (
                       <div className="space-y-2">
                         <LoadingButton
                           onClick={() => toggleBookAvailability(book.id, book.is_available)}
                           loading={isLoading('toggle-availability')}
                           loadingText="Mise à jour..."
                           className="w-full"
                           variant={book.is_available ? 'secondary' : 'success'}
                         >
                           {book.is_available ? (
                             <>
                               <X className="h-4 w-4 mr-2" />
                               Marquer indisponible
                             </>
                           ) : (
                             <>
                               <Check className="h-4 w-4 mr-2" />
                               Marquer disponible
                             </>
                           )}
                         </LoadingButton>
                         
                         {!book.rental_info && (
                           <LoadingButton
                             onClick={() => openDeleteModal(book.id, book.title)}
                             loading={isLoading('delete-book')}
                             loadingText="Suppression..."
                             className="w-full"
                             variant="danger"
                           >
                             <Trash2 className="h-4 w-4 mr-2" />
                             Supprimer
                           </LoadingButton>
                         )}
                       </div>
                     )}
                     
                     {/* Information de location */}
                     {activeTab === 'available' && !book.is_available && book.owner_id !== user?.id && book.rental_info && (
                       <div className="w-full space-y-1">
                         {/* Badge principal */}
                         <div className={`w-full inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg ${
                           isOverdue(book.rental_info.end_date) 
                             ? 'bg-orange-50 text-orange-700 border border-orange-200' 
                             : 'bg-orange-50 text-orange-700'
                         }`}>
                           {isOverdue(book.rental_info.end_date) ? (
                             <>
                               <AlertTriangle className="h-4 w-4 mr-1.5" />
                               <span>En retard</span>
                             </>
                           ) : (
                             <>
                               <Clock className="h-4 w-4 mr-1.5" />
                               <span>Loué jusqu'au {formatRentalDate(book.rental_info.end_date)}</span>
                             </>
                           )}
                         </div>
                         
                         {/* Détails du retard (seulement si en retard) */}
                         {isOverdue(book.rental_info.end_date) && (
                           <div className="text-xs text-orange-600 text-center">
                             {formatOverdueInfo(book.rental_info.end_date)}
                           </div>
                         )}
                       </div>
                     )}
                   </div>
                 </div>
               </div>
             ))}
           </div>
        ) : (
          <div className="space-y-4">
            {(activeTab === 'available' ? filteredAvailableBooks : filteredMyBooks).map((book) => (
              <div key={book.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-200">
                <div className="flex items-start gap-4">
                  {/* Image du livre */}
                  <div className="flex-shrink-0">
                    <Link to={`/book/${book.id}`} className="block relative">
                      <div className="w-16 h-20 overflow-hidden rounded-lg">
                        {book.cover_image ? (
                          <img
                            src={book.cover_image}
                            alt={book.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                            <BookOpen className="h-6 w-6 text-gray-300" />
                          </div>
                        )}
                      </div>
                      
                      {/* Badges */}
                      <div className="absolute -top-1 -right-1 flex flex-col gap-1">
                        {activeTab === 'available' && book.owner_id === user?.id && (
                          <span className="bg-green-500 text-white px-1.5 py-0.5 rounded-full text-xs font-medium">
                            Mon livre
                          </span>
                        )}
                        {(book.rental_count || 0) >= 3 && (
                          <span className="bg-yellow-500 text-white px-1.5 py-0.5 rounded-full text-xs font-medium flex items-center">
                            <TrendingUp className="h-2 w-2 mr-0.5" />
                            Pop
                          </span>
                        )}
                      </div>
                    </Link>
                    
                    {/* Bouton favori sous l'image */}
                    {activeTab === 'available' && book.owner_id !== user?.id && (
                      <div className="flex justify-center mt-2">
                        <FavoriteButton 
                          bookId={book.id} 
                          size="sm" 
                          showCount={false}
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Contenu */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <Link to={`/book/${book.id}`} className="block">
                          <h3 className="font-semibold text-gray-900 hover:text-forest-700 transition-colors mb-1 text-base line-clamp-2">
                            {book.title}
                          </h3>
                          <p className="text-sm text-gray-600 mb-2 line-clamp-1">
                            {book.author}
                          </p>
                        </Link>
                        
                        {/* Informations propriétaire */}
                        {activeTab === 'available' && book.owner_name && (
                          <div className="mb-3 space-y-1">
                            <div className="flex items-center text-sm text-gray-600">
                              <User className="h-4 w-4 mr-1.5 flex-shrink-0" />
                              <span className="truncate">{formatOwnerName(book.owner_name)}</span>
                            </div>
                            {book.owner_city && (
                              <div className="flex items-center text-sm text-gray-500">
                                <MapPin className="h-4 w-4 mr-1.5 flex-shrink-0" />
                                <span className="truncate">{book.owner_city}</span>
                              </div>
                            )}
                            {book.distance_formatted && (
                              <div className="flex items-center text-sm text-forest-700 font-medium">
                                <MapPin className="h-4 w-4 mr-1.5 flex-shrink-0" />
                                <span>{book.distance_formatted}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Statut */}
                        <div className="flex items-center gap-2 mb-4">
                          {book.is_available ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Disponible
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Non disponible
                            </span>
                          )}
                          
                          {activeTab === 'available' && book.is_available && book.has_requested && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-forest-100 text-forest-800">
                              Demande envoyée
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-col gap-2 ml-4">
                        {activeTab === 'available' && book.is_available && book.owner_id !== user?.id && (
                          <LoadingButton
                            onClick={() => requestBook(book.id)}
                            loading={isLoading('request-book')}
                            loadingText="Envoi en cours..."
                            disabled={book.has_requested}
                            variant={book.has_requested ? 'secondary' : 'primary'}
                          >
                            {book.has_requested ? (
                              <>
                                <Clock className="h-4 w-4 mr-2" />
                                Demande en cours
                              </>
                            ) : (
                              <>
                                <UserPlus className="h-4 w-4 mr-2" />
                                Demander
                              </>
                            )}
                          </LoadingButton>
                        )}
                        
                        
                        {activeTab === 'available' && book.owner_id === user?.id && (
                          <button
                            onClick={() => openFavoriteUsersModal(book.id, book.title)}
                            className="inline-flex items-center justify-center px-4 py-2 bg-forest-600 text-white text-sm font-medium rounded-xl hover:bg-forest-700 hover:shadow-md transition-all duration-200"
                          >
                            <Heart className="h-4 w-4 mr-2" />
                            Voir les intéressés
                          </button>
                        )}
                        
                        {activeTab === 'my-library' && (
                          <div className="flex flex-col gap-2">
                            <LoadingButton
                              onClick={() => toggleBookAvailability(book.id, book.is_available)}
                              loading={isLoading('toggle-availability')}
                              loadingText="Mise à jour..."
                              variant={book.is_available ? 'secondary' : 'success'}
                            >
                              {book.is_available ? (
                                <>
                                  <X className="h-4 w-4 mr-2" />
                                  Marquer indisponible
                                </>
                              ) : (
                                <>
                                  <Check className="h-4 w-4 mr-2" />
                                  Marquer disponible
                                </>
                              )}
                            </LoadingButton>
                            
                            {!book.rental_info && (
                              <LoadingButton
                                onClick={() => openDeleteModal(book.id, book.title)}
                                loading={isLoading('delete-book')}
                                loadingText="Suppression..."
                                variant="danger"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Supprimer
                              </LoadingButton>
                            )}
                          </div>
                        )}
                        
                        {activeTab === 'available' && !book.is_available && book.owner_id !== user?.id && book.rental_info && (
                          <div className="flex flex-col items-end space-y-1">
                            {/* Badge principal */}
                            <div className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg ${
                              isOverdue(book.rental_info.end_date) 
                                ? 'bg-orange-50 text-orange-700 border border-orange-200' 
                                : 'bg-orange-50 text-orange-700'
                            }`}>
                              {isOverdue(book.rental_info.end_date) ? (
                                <>
                                  <AlertTriangle className="h-4 w-4 mr-1.5" />
                                  <span>En retard</span>
                                </>
                              ) : (
                                <>
                                  <Clock className="h-4 w-4 mr-1.5" />
                                  <span>Loué jusqu'au {formatRentalDate(book.rental_info.end_date)}</span>
                                </>
                              )}
                            </div>
                            
                            {/* Détails du retard (seulement si en retard) */}
                            {isOverdue(book.rental_info.end_date) && (
                              <div className="text-xs text-orange-600 text-right">
                                {formatOverdueInfo(book.rental_info.end_date)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {(activeTab === 'available' ? filteredAvailableBooks : filteredMyBooks).length === 0 && (
          <div className="text-center py-16">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 max-w-md mx-auto">
              <BookOpen className="mx-auto h-16 w-16 text-gray-400 mb-6" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {activeTab === 'available' ? 'Aucun livre disponible' : 'Votre bibliothèque est vide'}
              </h3>
              <p className="text-gray-600 mb-6">
                {activeTab === 'available' 
                  ? 'Aucun livre ne correspond à votre recherche. Essayez de modifier vos critères.'
                  : 'Commencez par ajouter des livres à votre bibliothèque pour les partager avec la communauté.'
                }
              </p>
              {activeTab === 'my-library' && (
                <Link
                  to="/add-book"
                  className="btn btn-primary"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Ajouter un livre
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationDialog
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteBook}
        title="Supprimer le livre"
        message="Êtes-vous sûr de vouloir supprimer ce livre de votre bibliothèque ?"
        itemName={deleteModal.bookTitle}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
        details={[
          "Le livre de votre bibliothèque",
          "Toutes les demandes d'emprunt en cours",
          "Tous les avis liés à ce livre",
          "L'image de couverture"
        ]}
      />

      {/* Favorite Users Modal */}
      <FavoriteUsersModal
        isOpen={favoriteUsersModal.isOpen}
        onClose={closeFavoriteUsersModal}
        bookId={favoriteUsersModal.bookId}
        bookTitle={favoriteUsersModal.bookTitle}
      />
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
};

export default Library;